// FlowEdit.js
import * as React from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { ArrowBack, Warning as WarningIcon } from '@mui/icons-material';
import { useNavigate, useBlocker } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateTestEventAsync } from '../../redux/flows/updateEventSlice';
import { updateTestFlowAsync } from '../../redux/flows/updateFlowSlice';
import { fetchFlowWithDetailsAsync } from '../../redux/flows/getFlowsSlice';
import { deleteEventAsync } from '../../redux/flows/deleteEventSlice';

// Service imports
import { generateTempEventId } from '../../utilities/tempIdGenerator';
import {
  fetchAllFlows,
  fetchFlowDetails,
  filterFlowsByMarket,
} from '../../services/flowApiService';
import { selectMarket } from '../../redux/app/configSlice';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import FlowEventsSection from './FlowEventsSection';
import SchemaDragOverlay from './schemas/SchemaDragOverlay';
import { addSchemaToFlow } from '../../services/schemasService';
import {
  insertPendingSchemaEvent,
  resolveInsertIndex,
  getPendingSchemaEvents,
} from '../../utilities/flow/schemaEventHelpers';

export default function FlowEdit({ flowId, initialFlowData }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get current user's market from Redux
  const currentMarket = useSelector(selectMarket);

  // dnd-kit sensors — require 8px movement before a drag starts (avoids accidental drags on click).
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const [editingFlow, setEditingFlow] = React.useState({
    flow_name: '',
    flow_description: '',
    // test_case_name: "",
    test_case_id: '',
    events: [],
    isDraft: false,
  });

  // Predefined flow name options
  const flowNameOptions = [
    'Single Event',
    'Ad-Hoc Test',
    'TF1 - GMA - Homepage',
    'TF2 - GMA - Log In',
    'TF3 - GMA - Checkout – Pickup',
    'TF4 - GMA - Deals',
    'TF5 - GMA - Menu',
    'TF6 - GMA - Location',
    'TF7 - GMA - More & Recents',
    'TF8 - GMA - Loyalty',
    'TF9 - GMA – Delivery',
    'TF10 - GMA - Suggestive Sell',
    'TF11 - GMA - Store Closed',
    'TF12 - GMA - User & Loyalty objects',
    'TF13 - GMA – Registration',
    'TF14 - GMA - Checkout - Delivery',
    'TF15 - GMA - Recents & Faves',
    'TF16 - GMA - Code',
  ];

  const [originalFlow, setOriginalFlow] = React.useState(null);
  const [activeDragItem, setActiveDragItem] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [showSpinner, setShowSpinner] = React.useState(false);
  const [contentReady, setContentReady] = React.useState(false);
  const [contentVisible, setContentVisible] = React.useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = React.useState(false);
  const [pendingNavigation, setPendingNavigation] = React.useState(null);
  const [isDiscarding, setIsDiscarding] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [currentMarketFlows, setCurrentMarketFlows] = React.useState([]);
  const [testCaseIdError, setTestCaseIdError] = React.useState('');

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
  );

  // Handle the blocked navigation
  React.useEffect(() => {
    if (blocker.state === 'blocked') {
      // Show the unsaved dialog
      setShowUnsavedDialog(true);
      setPendingNavigation(() => () => {
        blocker.proceed();
      });
    }
  }, [blocker]);

  // Handle market changes - show unsaved dialog if there are unsaved changes
  React.useEffect(() => {
    const handleMarketChange = () => {
      if (hasUnsavedChanges) {
        setShowUnsavedDialog(true);
        setPendingNavigation(() => () => {
          navigate('/flows');
        });
      } else {
        // No unsaved changes, navigate immediately
        navigate('/flows');
      }
    };

    window.addEventListener('marketChanged', handleMarketChange);

    return () => {
      window.removeEventListener('marketChanged', handleMarketChange);
    };
  }, [hasUnsavedChanges, navigate]);

  // Control spinner visibility with delay to prevent flicker on fast loads
  React.useEffect(() => {
    let timer;
    if (isLoading) {
      // Only show spinner after 300ms delay
      timer = setTimeout(() => setShowSpinner(true), 300);
    } else {
      setShowSpinner(false);
    }
    return () => clearTimeout(timer);
  }, [isLoading]);

  // Load flow data from database (or use initialFlowData if provided by router)
  React.useEffect(() => {
    const loadFlow = async () => {
      if (flowId) {
        setIsLoading(true);
        setContentReady(false);
        setContentVisible(false);

        try {
          // Use initialFlowData if provided (from FlowEditRouter), otherwise fetch from database
          let fullFlow;
          if (initialFlowData && initialFlowData.events) {
            fullFlow = initialFlowData;
          } else {
            fullFlow = await dispatch(fetchFlowWithDetailsAsync(flowId)).unwrap();
          }

          const databaseFlow = JSON.parse(
            JSON.stringify({
              ...fullFlow,
              isDraft: false,
              isFromDatabase: true,
            }),
          );
          setEditingFlow(databaseFlow);
          setOriginalFlow(JSON.parse(JSON.stringify(databaseFlow)));
          setHasUnsavedChanges(false);
        } catch (error) {
          console.error('Error loading database flow:', error);
          alert(`Error loading flow: ${error.message || 'Please try again.'}`);
          navigate('/flows');
        }

        setIsLoading(false);
        // Two-step fade-in: first render at opacity 0, then fade to opacity 1
        setTimeout(() => {
          setContentReady(true);
          // Force browser to render at opacity 0 first with double RAF
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setContentVisible(true);
            });
          });
        }, 100);
      }
    };

    loadFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId, navigate, dispatch]); // initialFlowData intentionally excluded - only load once

  // Load flows for current market (for duplicate test case ID validation)
  React.useEffect(() => {
    const loadCurrentMarketFlows = async () => {
      try {
        // Get current market from localStorage (most reliable source)
        const storedMarket = localStorage.getItem('market');
        const market = storedMarket ? JSON.parse(storedMarket) : currentMarket;

        if (!market?.id) {
          console.warn('No market selected');
          return;
        }

        const allFlows = await fetchAllFlows();
        const filtered = filterFlowsByMarket(allFlows, market.id);
        setCurrentMarketFlows(filtered);
      } catch (error) {
        console.error('Error loading current market flows:', error);
      }
    };

    loadCurrentMarketFlows();
  }, [currentMarket]);

  // Validate test case ID for duplicates
  React.useEffect(() => {
    if (!editingFlow.test_case_id?.trim() || !originalFlow) {
      setTestCaseIdError('');
      return;
    }

    // Check if test case ID exists in current market (excluding this flow)
    const currentFlowDbId = Number(flowId);
    const isDuplicate = currentMarketFlows.some(
      (flow) =>
        flow.test_case_id?.toLowerCase() === editingFlow.test_case_id.trim().toLowerCase() &&
        flow.flow_db_id !== currentFlowDbId &&
        !isNaN(currentFlowDbId), // Safety check
    );

    if (isDuplicate) {
      setTestCaseIdError(
        'This test case ID already exists in your market. Please choose a different ID.',
      );
    } else {
      setTestCaseIdError('');
    }
  }, [editingFlow.test_case_id, currentMarketFlows, flowId, originalFlow]);

  // Track changes to detect unsaved modifications
  React.useEffect(() => {
    if (!originalFlow || !editingFlow || isLoading || isDiscarding) return;

    // Compare current state with original to detect changes
    const hasChanges =
      editingFlow.flow_name !== originalFlow.flow_name ||
      editingFlow.flow_description !== originalFlow.flow_description ||
      // editingFlow.test_case_name !== originalFlow.test_case_name ||
      editingFlow.test_case_id !== originalFlow.test_case_id ||
      JSON.stringify(editingFlow.events) !== JSON.stringify(originalFlow.events);

    setHasUnsavedChanges(hasChanges);
  }, [editingFlow, originalFlow, isLoading, isDiscarding]);

  // Add browser refresh/close warning for unsaved changes
  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        // Modern browsers require this specific approach
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        // Most browsers will show their own generic message
        // Custom messages are largely ignored for security reasons
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Prevent navigation with unsaved changes
  const handleNavigation = (navigationFn) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => navigationFn);
      setShowUnsavedDialog(true);
    } else {
      navigationFn();
    }
  };

  const handleConfirmNavigation = () => {
    // Set flag to prevent any final state updates
    setIsDiscarding(true);

    // Close dialog immediately
    setShowUnsavedDialog(false);

    // Reset states
    setHasUnsavedChanges(false);

    // Navigate after a brief delay to ensure cleanup
    setTimeout(() => {
      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
      setIsDiscarding(false);
    }, 50);
  };

  const handleCancelNavigation = () => {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
    // Reset the blocker if it was triggered
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const handleSaveFlow = async (andNavigate = false) => {
    if (!editingFlow.flow_name || !flowNameOptions.includes(editingFlow.flow_name)) {
      alert('Please select a valid flow name from the dropdown.');
      return;
    }

    if (testCaseIdError) {
      alert('Please fix validation errors before saving:\n' + testCaseIdError);
      return;
    }

    // Require test_case_id to be filled
    if (!editingFlow.test_case_id?.trim()) {
      alert('Test Case ID is required. Please enter a unique identifier for this test case.');
      return;
    }

    try {
      setIsSaving(true);

      // STEP 1: Handle DELETE operations FIRST - deleted events from database
      // This must happen before resequencing to avoid duplicate sequence numbers
      if (originalFlow && originalFlow.events) {
        const deletedEvents = [];

        // Find events that existed in original but not in current
        originalFlow.events.forEach((originalEvent) => {
          const stillExists = editingFlow.events.find((e) => e.event_id === originalEvent.event_id);

          // If event is missing and has database ID, mark for deletion
          if (!stillExists && originalEvent.event_db_id) {
            deletedEvents.push(originalEvent);
          }
        });

        // Delete events from database (backend will cascade delete scenarios/attributes)
        if (deletedEvents.length > 0) {
          const deletePromises = deletedEvents.map((event) =>
            dispatch(deleteEventAsync(event.event_db_id))
              .then((result) => ({
                type: 'DELETE_EVENT',
                eventId: event.event_db_id,
                success: result.success,
                error: result.error,
              }))
              .catch((error) => ({
                type: 'DELETE_EVENT',
                eventId: event.event_db_id,
                success: false,
                error: error.message,
              })),
          );

          const deleteResults = await Promise.all(deletePromises);

          // Check for failures
          const failures = deleteResults.filter((r) => !r.success);
          if (failures.length > 0) {
            console.error('Some event deletions failed:', failures);
            alert(`Warning: ${failures.length} event(s) could not be deleted from database.`);
          }
        }
      }

      // STEP 2: Update flow metadata (name, description, test_case_id)
      let flowUpdateResult = { success: true };
      if (originalFlow) {
        const flowChanges = {};
        if (editingFlow.flow_name !== originalFlow.flow_name) {
          flowChanges.flow_name = editingFlow.flow_name;
        }
        if (editingFlow.flow_description !== originalFlow.flow_description) {
          flowChanges.flow_description = editingFlow.flow_description;
        }
        if (editingFlow.test_case_id !== originalFlow.test_case_id) {
          flowChanges.test_case_id = editingFlow.test_case_id;
        }

        // If there are flow changes, update in database
        if (Object.keys(flowChanges).length > 0) {
          flowUpdateResult = await dispatch(updateTestFlowAsync(flowId, flowChanges));

          if (!flowUpdateResult.success) {
            console.error('Failed to update flow in database:', flowUpdateResult.error);
            alert(
              `Warning: Could not save flow changes to database (${flowUpdateResult.error}), but saving locally.`,
            );
          }
        }
      }

      // STEP 2b: Create pending schema-events (dropped but not yet persisted).
      const pendingSchemaEvents = getPendingSchemaEvents(editingFlow.events);
      if (pendingSchemaEvents.length > 0) {
        const createResults = await Promise.all(
          pendingSchemaEvents.map((event) =>
            addSchemaToFlow({
              schema_id: event.schema_id,
              test_flow_id: Number(flowId) || flowId,
              sequence: event.event_order,
            })
              .then(() => ({ success: true }))
              .catch((error) => ({ success: false, error: error.message })),
          ),
        );
        const createFailures = createResults.filter((r) => !r.success);
        if (createFailures.length > 0) {
          throw new Error(`Could not add ${createFailures.length} schema event(s) to this flow.`);
        }
      }

      // STEP 3: Update event orders (resequencing) - done AFTER deletions to avoid conflicts
      const eventOrderChanges = [];
      if (originalFlow && originalFlow.events) {
        editingFlow.events.forEach((currentEvent) => {
          // Find the corresponding original event
          const originalEvent = originalFlow.events.find(
            (s) => s.event_id === currentEvent.event_id,
          );

          // If the event exists in the original and has a different order, prepare update
          if (
            originalEvent &&
            originalEvent.event_order !== currentEvent.event_order &&
            currentEvent.event_db_id
          ) {
            eventOrderChanges.push({
              event: {
                operation: 'UPDATE',
                data: currentEvent,
                changedFields: {
                  sequence: currentEvent.event_order, // Database expects 'sequence' not 'order'
                },
              },
              scenarios: { create: [], update: [], delete: [] },
              attributes: { create: [], update: [], delete: [] },
            });
          }
        });
      }

      // Update event orders in the database if any changed
      if (eventOrderChanges.length > 0) {
        // Update all event orders
        const updatePromises = eventOrderChanges.map((changes) =>
          dispatch(updateTestEventAsync(changes)),
        );

        const results = await Promise.all(updatePromises);

        // Check if any failed
        const failed = results.filter((r) => !r.success);
        if (failed.length > 0) {
          console.error('Some event order updates failed:', failed);
          alert(`Warning: Some event orders could not be saved to database, but saving locally.`);
        }
      }

      // For database flows, all API updates are done above

      // Refetch the flow so newly created schema-events carry real database ids.
      const refreshed = await dispatch(fetchFlowWithDetailsAsync(flowId)).unwrap();
      const refreshedFlow = JSON.parse(
        JSON.stringify({ ...refreshed, isDraft: false, isFromDatabase: true }),
      );
      setEditingFlow(refreshedFlow);
      setOriginalFlow(JSON.parse(JSON.stringify(refreshedFlow)));
      setHasUnsavedChanges(false);

      // No need to update Redux cache - next navigation will fetch fresh data

      // Trigger FlowList refresh
      window.dispatchEvent(new Event('flowUpdated'));

      // If this was triggered from the navigation dialog
      if (andNavigate) {
        setShowUnsavedDialog(false);
        // Execute the pending navigation after a brief delay to ensure state updates
        setTimeout(() => {
          if (pendingNavigation) {
            pendingNavigation();
            setPendingNavigation(null);
          }
        }, 100);
      } else {
        if (eventOrderChanges.length > 0) {
          alert(`Flow saved successfully! Updated ${eventOrderChanges.length} event order(s).`);
        } else {
          alert('Flow saved successfully!');
        }
      }
    } catch (error) {
      console.error('Error saving flow:', error);
      alert(`Error saving flow: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddEvent = () => {
    const newEventId = generateTempEventId();

    const newEvent = {
      event_id: newEventId,
      event_name: 'New Event',
      event_order: editingFlow.events.length + 1,
      scenarios: [],
      isNew: true,
    };

    // Update React state
    const updatedFlow = {
      ...editingFlow,
      events: [...editingFlow.events, newEvent],
    };

    setEditingFlow(updatedFlow);

    // Pass the new event data via navigation state
    navigate(`/flows/edit/${flowId}/event/${newEventId}`, {
      state: {
        newEvent: newEvent,
        flowData: updatedFlow,
      },
    });
  };

  const handleEditEvent = (event) => {
    // Use database ID if available, otherwise use temporary ID
    const eventIdToUse = event.event_db_id || event.event_id;
    navigate(`/flows/edit/${flowId}/event/${eventIdToUse}`);
  };

  const handleDeleteEvent = (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      // Remove the event
      const remainingEvents = editingFlow.events.filter((event) => event.event_id !== eventId);

      // Resequence remaining events to close gaps (same pattern as handleDrop)
      const resequencedEvents = remainingEvents
        .sort((a, b) => a.event_order - b.event_order)
        .map((event, index) => ({
          ...event,
          event_order: index + 1,
        }));

      // ONLY update editingFlow (same pattern as scenario/attribute deletion)
      // DO NOT update originalFlow - handleSaveFlow needs to detect the deletion
      setEditingFlow((prev) => ({
        ...prev,
        events: resequencedEvents,
      }));

      // Deletion will be handled during save via handleSaveFlow
      // This works uniformly for both mock and database flows
    }
  };

  const handleDndDragStart = ({ active }) => {
    if (active.data.current?.type === 'schema') {
      setActiveDragItem({ type: 'schema', schema: active.data.current.schema });
    } else {
      const event = editingFlow.events.find((e) => e.event_id === active.id);
      if (event) setActiveDragItem({ type: 'flow-event', event });
    }
  };

  const handleDndDragEnd = ({ active, over }) => {
    setActiveDragItem(null);
    if (!over) return;

    // Schema dropped from the panel: stage a pending event at the drop position.
    if (active.data.current?.type === 'schema') {
      const schema = active.data.current.schema;
      const sorted = [...editingFlow.events].sort((a, b) => a.event_order - b.event_order);
      const insertIndex = resolveInsertIndex(sorted, over.id);
      setEditingFlow((prev) => ({
        ...prev,
        events: insertPendingSchemaEvent(prev.events, schema, insertIndex),
      }));
      return;
    }

    // Reorder existing events (staged until Save Flow).
    if (active.id !== over.id) {
      setEditingFlow((prev) => {
        const sorted = [...prev.events].sort((a, b) => a.event_order - b.event_order);
        const oldIndex = sorted.findIndex((e) => e.event_id === active.id);
        const newIndex = sorted.findIndex((e) => e.event_id === over.id);
        const reordered = arrayMove(sorted, oldIndex, newIndex);
        return { ...prev, events: reordered.map((e, i) => ({ ...e, event_order: i + 1 })) };
      });
    }
  };
  if (isLoading) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: showSpinner ? 1 : 0,
          transition: 'opacity 0.2s ease-in-out',
        }}
      >
        <CircularProgress
          size="4rem"
          sx={{
            color: 'secondary.main',
          }}
        />
      </Box>
    );
  }

  if (!contentReady) {
    return null; // Don't render at all until ready
  }

  const sortedEventIds = [...editingFlow.events]
    .sort((a, b) => a.event_order - b.event_order)
    .map((e) => e.event_id);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDndDragStart}
      onDragEnd={handleDndDragEnd}
    >
      <Box
        sx={{
          p: 3,
          opacity: contentVisible ? 1 : 0,
          transition: 'opacity 0.4s ease-in-out',
          willChange: 'opacity',
        }}
      >
        {/* Header with unsaved changes indicator */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => handleNavigation(() => navigate('/flows'))}
            sx={{ mr: 1, color: 'primary.main' }}
          >
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" component="h1">
                Edit Test Case: {editingFlow.flow_name}
              </Typography>
              {hasUnsavedChanges && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500 }}>
                    Unsaved changes
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Unsaved changes alert */}
        {hasUnsavedChanges && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            You have unsaved changes. Remember to save your work before leaving this page.
          </Alert>
        )}

        {/* Flow Details Form */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Test Case Details
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="flow-name-edit-label">Flow Name</InputLabel>
              <Select
                labelId="flow-name-edit-label"
                id="flow-name-edit-select"
                value={editingFlow.flow_name}
                label="Flow Name"
                onChange={(e) =>
                  setEditingFlow((prev) => ({
                    ...prev,
                    flow_name: e.target.value,
                  }))
                }
                sx={{
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'text.secondary',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select a flow type</em>
                </MenuItem>
                {flowNameOptions
                  .filter((option) => option !== 'Single Event')
                  .map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
              </Select>
              <FormHelperText>Select the type of flow</FormHelperText>
            </FormControl>
            {/* <TextField
            label="Test Case Name"
            value={editingFlow.test_case_name || ''}
            onChange={(e) =>
              setEditingFlow((prev) => ({
                ...prev,
                test_case_name: e.target.value,
              }))
            }
            fullWidth
            placeholder="e.g. Verify User Registration"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'text.secondary',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main',
              }
            }}
          /> */}
            <TextField
              label="Test Case ID"
              value={editingFlow.test_case_id || ''}
              onChange={(e) =>
                setEditingFlow((prev) => ({
                  ...prev,
                  test_case_id: e.target.value,
                }))
              }
              fullWidth
              required
              placeholder="e.g. TC-001"
              error={!!testCaseIdError}
              helperText={testCaseIdError || 'Required - Unique identifier for this test case'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: testCaseIdError ? 'error.main' : 'text.secondary',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: testCaseIdError ? 'error.main' : 'primary.main',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: testCaseIdError ? 'error.main' : 'primary.main',
                },
              }}
            />
            <TextField
              label="Test Case Description"
              value={editingFlow.flow_description}
              onChange={(e) =>
                setEditingFlow((prev) => ({
                  ...prev,
                  flow_description: e.target.value,
                }))
              }
              multiline
              rows={3}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'text.secondary',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'primary.main',
                },
              }}
            />
          </Box>
        </Paper>

        {/* Events Section */}
        <FlowEventsSection
          events={editingFlow.events}
          sortedEventIds={sortedEventIds}
          onAddEvent={handleAddEvent}
          onEditEvent={handleEditEvent}
          onDeleteEvent={handleDeleteEvent}
        />

        {/* Save/Cancel Actions */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => handleNavigation(() => navigate('/flows'))}
            sx={{
              borderColor: 'text.secondary',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'text.secondary',
                backgroundColor: 'action.hover',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveFlow}
            disabled={!hasUnsavedChanges || isSaving || !!testCaseIdError}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.light',
              },
              '&:disabled': {
                bgcolor: 'action.disabledBackground',
              },
            }}
          >
            {isSaving ? 'Saving...' : hasUnsavedChanges ? 'Save Flow' : 'Saved'}
          </Button>
        </Box>

        {/* Unsaved Changes Dialog */}
        <Dialog open={showUnsavedDialog} onClose={handleCancelNavigation}>
          <DialogTitle>Unsaved Changes</DialogTitle>
          <DialogContent>
            <Typography>
              You have unsaved changes that will be lost. Are you sure you want to leave without
              saving?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCancelNavigation}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              Stay
            </Button>
            <Button
              onClick={() => handleSaveFlow(true)}
              variant="contained"
              disabled={isSaving || !!testCaseIdError}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.light',
                },
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                },
              }}
            >
              {isSaving ? 'Saving...' : 'Save & Continue'}
            </Button>
            <Button onClick={handleConfirmNavigation} color="error">
              Discard Changes
            </Button>
          </DialogActions>
        </Dialog>
        <Box
          sx={{
            p: 3,
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.2s ease-in-out',
          }}
        ></Box>
      </Box>
      <DragOverlay>
        <SchemaDragOverlay activeDragItem={activeDragItem} />
      </DragOverlay>
    </DndContext>
  );
}
